/**
 * Redux hooks for API state management
 * These hooks provide a consistent interface for managing application state
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createMicroPractice, createModule, createPlaylist, createProgram, createProgramCategory, createResource, createResourceCategory, createUser, deleteMicroPractice, deleteModule, deletePlaylist, deleteProgram, deleteProgramCategory, deleteResource, deleteResourceCategory, deleteUser, fetchMicroPractices, fetchModules, fetchPlaylists, fetchProgramCategories, fetchPrograms, fetchResourceCategories, fetchResources, fetchSettings, fetchUsers, RootState, updateMicroPracticeAction, updateModuleAction, updatePlaylistAction, updateProgramAction, updateProgramCategoryAction, updateResourceAction, updateResourceCategoryAction, updateSettings, updateUserAction, uploadModuleVideo } from '../store';
import { fetchSubscriptionAnalytics } from '../store/slices/subscriptionSlice';
import { fetchPlaylistAnalytics } from '../store/slices/playlistAnalyticsSlice';
import { fetchCheckInAnalytics } from '../store/slices/checkInAnalyticsSlice';
import { fetchJournalAnalytics } from '../store/slices/journalAnalyticsSlice';
import { fetchOverviewCounts } from '../store/slices/overviewCountsSlice';
import { fetchRoles, fetchRole, createRole, updateRoleAction, deleteRole, assignRoleToUser, removeRoleFromUser, getUsersByRole } from '../pages/Roles/rolesSlice';
import { fetchPermissions, fetchPermission, createPermission, updatePermissionAction, deletePermission, fetchPermissionsByResource } from '../pages/Permissions/permissionsSlice';
import { Category, MicroPractice, Module, Playlist, Program, Resource, User } from '../store/slices/types';

// Modules Hook
export const useModules = (page?: number, limit?: number) => {
  const dispatch = useDispatch();
  const modules = useSelector((state: RootState) => state.modules.modules);
  const loading = useSelector((state: RootState) => state.modules.loading);
  const error = useSelector((state: RootState) => state.modules.error);
  const uploadProgress = useSelector((state: RootState) => state.modules.uploadProgress);
  const pagination = useSelector((state: RootState) => state.modules.pagination);

  useEffect(() => {
    dispatch(fetchModules({ page: page || 1, limit: limit || 10, activeOnly: false }));
  }, [dispatch, page, limit]);

  const createModuleAction = async (module: Omit<Module, '_id' | 'id'>) => {
    dispatch(createModule(module));
  };

  const updateModuleActionWrapper = async (id: string, module: Partial<Module>) => {
    dispatch(updateModuleAction({ id, data: module }));
  };

  const deleteModuleAction = async (id: string) => {
    dispatch(deleteModule(id));
  };

  const uploadVideoAction = async (id: string, file: File) => {
    dispatch(uploadModuleVideo({ id, file }));
  };

  return {
    modules,
    loading,
    error,
    uploadProgress,
    pagination,
    createModule: createModuleAction,
    updateModule: updateModuleActionWrapper,
    deleteModule: deleteModuleAction,
    uploadVideo: uploadVideoAction,
    refetch: () => dispatch(fetchModules({ page: page || 1, limit: limit || 10, activeOnly: false })),
  };
};

// Programs Hook
export const usePrograms = (page?: number, limit?: number) => {
  const dispatch = useDispatch();
  const programs = useSelector((state: RootState) => state.programs.programs);
  const loading = useSelector((state: RootState) => state.programs.loading);
  const error = useSelector((state: RootState) => state.programs.error);
  const pagination = useSelector((state: RootState) => state.programs.pagination);

  useEffect(() => {
    dispatch(fetchPrograms({ page: page || 1, limit: limit || 10 }));
  }, [dispatch, page, limit]);

  const createProgramAction = async (program: Omit<Program, '_id' | 'id'>) => {
    dispatch(createProgram(program));
  };

  const updateProgramActionWrapper = async (id: string, program: Partial<Program>) => {
    dispatch(updateProgramAction({ id, data: program }));
  };

  const deleteProgramAction = async (id: string) => {
    dispatch(deleteProgram(id));
  };

  return {
    programs,
    loading,
    error,
    pagination,
    createProgram: createProgramAction,
    updateProgram: updateProgramActionWrapper,
    deleteProgram: deleteProgramAction,
    refetch: () => dispatch(fetchPrograms({ page: page || 1, limit: limit || 10 })),
  };
};

// Resources Hook
export const useResources = (page?: number, limit?: number) => {
  const dispatch = useDispatch();
  const resources = useSelector((state: RootState) => state.resources.resources);
  const loading = useSelector((state: RootState) => state.resources.loading);
  const error = useSelector((state: RootState) => state.resources.error);
  const uploadProgress = useSelector((state: RootState) => state.resources.uploadProgress);
  const pagination = useSelector((state: RootState) => state.resources.pagination);

  useEffect(() => {
    dispatch(fetchResources({ page: page || 1, limit: limit || 10 }));
  }, [dispatch, page, limit]);

  const createResourceAction = async (resource: Omit<Resource, '_id' | 'id'>) => {
    dispatch(createResource(resource));
  };

  const updateResourceActionWrapper = async (id: string, resource: Partial<Resource>) => {
    dispatch(updateResourceAction({ id, data: resource }));
  };

  const deleteResourceAction = async (id: string) => {
    dispatch(deleteResource(id));
  };

  return {
    resources,
    loading,
    error,
    uploadProgress,
    pagination,
    createResource: createResourceAction,
    updateResource: updateResourceActionWrapper,
    deleteResource: deleteResourceAction,
    refetch: () => dispatch(fetchResources({ page: page || 1, limit: limit || 10 })),
  };
};

// Micro Practices Hook
export const useMicroPractices = (page?: number, limit?: number) => {
  const dispatch = useDispatch();
  const microPractices = useSelector((state: RootState) => state.microPractices.microPractices);
  const loading = useSelector((state: RootState) => state.microPractices.loading);
  const error = useSelector((state: RootState) => state.microPractices.error);
  const pagination = useSelector((state: RootState) => state.microPractices.pagination);

  useEffect(() => {
    dispatch(fetchMicroPractices({ page: page || 1, limit: limit || 10, activeOnly: false }));
  }, [dispatch, page, limit]);

  const createMicroPracticeAction = async (microPractice: Omit<MicroPractice, '_id' | 'id'>) => {
    dispatch(createMicroPractice(microPractice));
  };

  const updateMicroPracticeActionWrapper = async (id: string, microPractice: Partial<MicroPractice>) => {
    dispatch(updateMicroPracticeAction({ id, data: microPractice }));
  };

  const deleteMicroPracticeAction = async (id: string) => {
    dispatch(deleteMicroPractice(id));
  };

  return {
    microPractices,
    loading,
    error,
    pagination,
    createMicroPractice: createMicroPracticeAction,
    updateMicroPractice: updateMicroPracticeActionWrapper,
    deleteMicroPractice: deleteMicroPracticeAction,
    refetch: () => dispatch(fetchMicroPractices({ page: page || 1, limit: limit || 10, activeOnly: false })),
  };
};

// Playlists Hook
export const usePlaylists = () => {
  const dispatch = useDispatch();
  const playlists = useSelector((state: RootState) => state.playlists.playlists);
  const loading = useSelector((state: RootState) => state.playlists.loading);
  const error = useSelector((state: RootState) => state.playlists.error);

  useEffect(() => {
    dispatch(fetchPlaylists());
  }, [dispatch]);

  const createPlaylistAction = async (playlist: Omit<Playlist, '_id' | 'id'>) => {
    dispatch(createPlaylist(playlist));
  };

  const updatePlaylistActionWrapper = async (id: string, playlist: Partial<Playlist>) => {
    dispatch(updatePlaylistAction({ id, data: playlist }));
  };

  const deletePlaylistAction = async (id: string) => {
    dispatch(deletePlaylist(id));
  };

  return {
    playlists,
    loading,
    error,
    createPlaylist: createPlaylistAction,
    updatePlaylist: updatePlaylistActionWrapper,
    deletePlaylist: deletePlaylistAction,
    refetch: () => dispatch(fetchPlaylists()),
  };
};

// Users Hook
export const useUsers = (page?: number, limit?: number) => {
  const dispatch = useDispatch();
  const users = useSelector((state: RootState) => state.users.users);
  const loading = useSelector((state: RootState) => state.users.loading);
  const error = useSelector((state: RootState) => state.users.error);
  const pagination = useSelector((state: RootState) => state.users.pagination);

  useEffect(() => {
    dispatch(fetchUsers({ page: page || 1, limit: limit || 10 }));
  }, [dispatch, page, limit]);

  const createUserAction = async (user: Omit<User, '_id' | 'id' | 'uid'>) => {
    dispatch(createUser(user));
  };

  const updateUserActionWrapper = async (id: string, user: Partial<User>) => {
    dispatch(updateUserAction({ id, data: user }));
  };

  const deleteUserAction = async (id: string) => {
    dispatch(deleteUser(id));
  };

  return {
    users,
    loading,
    error,
    pagination,
    createUser: createUserAction,
    updateUser: updateUserActionWrapper,
    deleteUser: deleteUserAction,
    refetch: () => dispatch(fetchUsers({ page: page || 1, limit: limit || 10 })),
  };
};

// Settings Hook
export const useSettings = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state: RootState) => state.settings.settings);
  const loading = useSelector((state: RootState) => state.settings.loading);
  const error = useSelector((state: RootState) => state.settings.error);

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  const updateSettingsAction = async (newSettings: any) => {
    dispatch(updateSettings(newSettings));
  };

  return {
    settings,
    loading,
    error,
    updateSettings: updateSettingsAction,
    refetch: () => dispatch(fetchSettings()),
  };
};

// Program Categories Hook
export const useProgramCategories = () => {
  const dispatch = useDispatch();
  const categories = useSelector((state: RootState) => state.programCategories.categories);
  const loading = useSelector((state: RootState) => state.programCategories.loading);
  const error = useSelector((state: RootState) => state.programCategories.error);

  useEffect(() => {
    // Only fetch if categories are empty and not currently loading
    if (categories.length === 0 && !loading) {
      dispatch(fetchProgramCategories());
    }
  }, [dispatch, categories.length, loading]);

  const createCategoryAction = async (category: Omit<Category, '_id' | 'id'>) => {
    dispatch(createProgramCategory(category));
  };

  const updateCategoryActionWrapper = async (id: string, category: Partial<Category>) => {
    dispatch(updateProgramCategoryAction({ id, data: category }));
  };

  const deleteCategoryAction = async (id: string) => {
    dispatch(deleteProgramCategory(id));
  };

  return {
    categories,
    loading,
    error,
    createCategory: createCategoryAction,
    updateCategory: updateCategoryActionWrapper,
    deleteCategory: deleteCategoryAction,
    refetch: () => dispatch(fetchProgramCategories()),
  };
};

// Resource Categories Hook
export const useResourceCategories = () => {
  const dispatch = useDispatch();
  const categories = useSelector((state: RootState) => state.resourceCategories.categories);
  const loading = useSelector((state: RootState) => state.resourceCategories.loading);
  const error = useSelector((state: RootState) => state.resourceCategories.error);

  useEffect(() => {
    dispatch(fetchResourceCategories());
  }, [dispatch]);

  const createCategoryAction = async (category: Omit<Category, '_id' | 'id'>) => {
    dispatch(createResourceCategory(category));
  };

  const updateCategoryActionWrapper = async (id: string, category: Partial<Category>) => {
    dispatch(updateResourceCategoryAction({ id, data: category }));
  };

  const deleteCategoryAction = async (id: string) => {
    dispatch(deleteResourceCategory(id));
  };

  return {
    categories,
    loading,
    error,
    createCategory: createCategoryAction,
    updateCategory: updateCategoryActionWrapper,
    deleteCategory: deleteCategoryAction,
    refetch: () => dispatch(fetchResourceCategories()),
  };
};

// Combined Categories Hook (for backward compatibility)
export const useCategories = () => {
  const programCategories = useProgramCategories();
  const resourceCategories = useResourceCategories();
  const location = window.location.pathname;
  const isProgramCategories = location.includes('program-categories');
  const isResourceCategories = location.includes('resource-categories');

  // Return the appropriate hook based on the route
  if (isProgramCategories) {
    return programCategories;
  } else if (isResourceCategories) {
    return resourceCategories;
  }
  // Default to program categories
  return programCategories;
};

// Subscription Hook
export const useSubscription = () => {
  const dispatch = useDispatch();
  const subscriptionAnalytics = useSelector((state: RootState) => state.subscription.subscriptionAnalytics);
  const loading = useSelector((state: RootState) => state.subscription.loading);
  const error = useSelector((state: RootState) => state.subscription.error);

  useEffect(() => {
    dispatch(fetchSubscriptionAnalytics());
  }, [dispatch]);

  return {
    subscriptionAnalytics,
    loading,
    error,
    refetch: () => dispatch(fetchSubscriptionAnalytics()),
  };
};

// Playlist Analytics Hook
export const usePlaylistAnalytics = () => {
  const dispatch = useDispatch();
  const playlistAnalytics = useSelector((state: RootState) => state.playlistAnalytics.playlistAnalytics);
  const loading = useSelector((state: RootState) => state.playlistAnalytics.loading);
  const error = useSelector((state: RootState) => state.playlistAnalytics.error);

  useEffect(() => {
    dispatch(fetchPlaylistAnalytics());
  }, [dispatch]);

  return {
    playlistAnalytics,
    loading,
    error,
    refetch: () => dispatch(fetchPlaylistAnalytics()),
  };
};

// Check-In Analytics Hook
export const useCheckInAnalytics = () => {
  const dispatch = useDispatch();
  const checkInAnalytics = useSelector((state: RootState) => state.checkInAnalytics.checkInAnalytics);
  const loading = useSelector((state: RootState) => state.checkInAnalytics.loading);
  const error = useSelector((state: RootState) => state.checkInAnalytics.error);

  useEffect(() => {
    dispatch(fetchCheckInAnalytics());
  }, [dispatch]);

  return {
    checkInAnalytics,
    loading,
    error,
    refetch: () => dispatch(fetchCheckInAnalytics()),
  };
};

// Journal Analytics Hook
export const useJournalAnalytics = () => {
  const dispatch = useDispatch();
  const journalAnalytics = useSelector((state: RootState) => state.journalAnalytics.journalAnalytics);
  const loading = useSelector((state: RootState) => state.journalAnalytics.loading);
  const error = useSelector((state: RootState) => state.journalAnalytics.error);

  useEffect(() => {
    dispatch(fetchJournalAnalytics());
  }, [dispatch]);

  return {
    journalAnalytics,
    loading,
    error,
    refetch: () => dispatch(fetchJournalAnalytics()),
  };
};

// Overview Counts Hook
export const useOverviewCounts = () => {
  const dispatch = useDispatch();
  const overviewCounts = useSelector((state: RootState) => state.overviewCounts.overviewCounts);
  const loading = useSelector((state: RootState) => state.overviewCounts.loading);
  const error = useSelector((state: RootState) => state.overviewCounts.error);

  useEffect(() => {
    dispatch(fetchOverviewCounts());
  }, [dispatch]);

  return {
    overviewCounts,
    loading,
    error,
    refetch: () => dispatch(fetchOverviewCounts()),
  };
};

// Roles Hook
export const useRoles = () => {
  const dispatch = useDispatch();
  const roles = useSelector((state: RootState) => state.roles.roles);
  const currentRole = useSelector((state: RootState) => state.roles.currentRole);
  const loading = useSelector((state: RootState) => state.roles.loading);
  const error = useSelector((state: RootState) => state.roles.error);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  return {
    roles,
    currentRole,
    loading,
    error,
    refetch: () => dispatch(fetchRoles()),
    fetchRole: (id: string) => dispatch(fetchRole(id)),
    createRole: (role: any) => dispatch(createRole(role)),
    updateRole: (id: string, data: any) => dispatch(updateRoleAction({ id, data })),
    deleteRole: (id: string) => dispatch(deleteRole(id)),
    assignRoleToUser: (userId: string, roleId: string) => dispatch(assignRoleToUser({ userId, roleId })),
    removeRoleFromUser: (userId: string) => dispatch(removeRoleFromUser(userId)),
    getUsersByRole: (roleId: string) => dispatch(getUsersByRole(roleId)),
  };
};

// Permissions Hook
export const usePermissions = () => {
  const dispatch = useDispatch();
  const permissions = useSelector((state: RootState) => state.permissions.permissions);
  const currentPermission = useSelector((state: RootState) => state.permissions.currentPermission);
  const loading = useSelector((state: RootState) => state.permissions.loading);
  const error = useSelector((state: RootState) => state.permissions.error);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  return {
    permissions,
    currentPermission,
    loading,
    error,
    refetch: () => dispatch(fetchPermissions()),
    fetchPermission: (id: string) => dispatch(fetchPermission(id)),
    createPermission: (permission: any) => dispatch(createPermission(permission)),
    updatePermission: (id: string, data: any) => dispatch(updatePermissionAction({ id, data })),
    deletePermission: (id: string) => dispatch(deletePermission(id)),
    fetchByResource: (resource: string) => dispatch(fetchPermissionsByResource(resource)),
  };
};

